import React from 'react';
import { useHistory } from 'react-router-dom';
import { FiArrowLeft, FiSearch, FiHelpCircle, FiCheck, FiChevronDown } from 'react-icons/fi';
import { useClient } from '../clientSettings/clientSettingsData';

// the client's site as the hub prints it: "49metals.com.au"
export const useHubSite = () => {
    const { stock, short, ticker, name } = useClient();
    const host = (stock?.websiteUrl || 'https://49metals.com.au').replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '');
    return { host, short, ticker, name, url: (path = '/') => `https://${host}${path}` };
};

export const hubPath = (...parts: string[]) => `/website-hub${parts.filter(Boolean).map(p => `/${p}`).join('')}`;

// title + subtitle on the left, buttons on the right (Account.scss .page-header)
export const HubHeader: React.FC<{ title: string; sub?: React.ReactNode }> = ({ title, sub, children }) =>
    <div className="page-header hub-header">
        <div>
            <h1>{title}</h1>
            {sub && <p>{sub}</p>}
        </div>
        {children && <div className="action-top">{children}</div>}
    </div>;

export const BackLink: React.FC<{ to: string; label: string }> = ({ to, label }) => {
    const history = useHistory();
    return <button type="button" className="link hub-back" onClick={() => history.push(to)}><FiArrowLeft /> {label}</button>;
};

// cyan tip at the foot of a page, with the help icon tile
export const HubTip: React.FC = ({ children }) =>
    <div className="hub-tip"><span className="icon-tile"><FiHelpCircle /></span><p>{children}</p></div>;

// cyan banner with an icon, a bold line, a sub line and an optional pill on the right (e.g. the WordPress note)
export const HubNote: React.FC<{ icon: React.ReactNode; title: string; pill?: string }> = ({ icon, title, pill, children }) =>
    <div className="hub-note">
        <span className="ic">{icon}</span>
        <div className="t"><strong>{title}</strong><span>{children}</span></div>
        {pill && <span className="pill-ok"><FiCheck /> {pill}</span>}
    </div>;

export const SearchBox: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) =>
    <label className="hub-search">
        <FiSearch />
        <input value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </label>;

export interface Chip<T extends string> { value: T; label: string; count?: number }

export function FilterChips<T extends string>({ chips, value, onChange }: { chips: Chip<T>[]; value: T; onChange: (v: T) => void }) {
    return <div className="hub-chips" role="tablist">
        {chips.map(c => <button key={c.value} type="button" role="tab" aria-selected={c.value === value}
            className={c.value === value ? 'active' : ''} onClick={() => onChange(c.value)}>
            {c.label}{c.count !== undefined && <span>{c.count}</span>}
        </button>)}
    </div>;
}

// a native select drawn like the spec's "All statuses ⌄" box
export const SelectBox: React.FC<{ value: string; options: string[]; onChange: (v: string) => void; label: string; className?: string }> = ({ value, options, onChange, label, className }) =>
    <span className={`hub-select${className ? ` ${className}` : ''}`}>
        <select aria-label={label} value={value} onChange={e => onChange(e.target.value)}>
            {options.map(o => <option key={o}>{o}</option>)}
        </select>
        <FiChevronDown />
    </span>;

// 34×34 outlined icon button for table rows; danger = red icon (trash)
export const IconButton: React.FC<{ label: string; onClick?: () => void; danger?: boolean; href?: string }> = ({ label, onClick, danger, href, children }) =>
    href
        ? <a className={`hub-icon-btn${danger ? ' danger' : ''}`} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} onClick={e => e.stopPropagation()}>{children}</a>
        : <button type="button" className={`hub-icon-btn${danger ? ' danger' : ''}`} aria-label={label} title={label} onClick={e => { e.stopPropagation(); onClick?.(); }}>{children}</button>;

export const Pager: React.FC<{ shown: number; total: number; noun: string }> = ({ shown, total, noun }) =>
    <div className="hub-pager">
        <span>Showing 1–{shown} of {total} {noun}</span>
        <button type="button" className="btn-outline" disabled>Previous</button>
        <button type="button" className="btn-outline" disabled={shown >= total}>Next</button>
    </div>;

// the four-up stat cards (Overview, Contact forms)
export const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sub: string; badge?: React.ReactNode }> = ({ icon, label, value, sub, badge }) =>
    <div className="card hub-stat">
        <div className="h"><span className="icon-tile">{icon}</span><span className="label">{label}</span>{badge}</div>
        <strong>{value}</strong>
        <p>{sub}</p>
    </div>;

// status / type pill; tone picks the colours (see .hub-pill in WebsiteHub.scss)
export type PillTone = 'grey' | 'green' | 'cyan' | 'amber' | 'red' | 'violet' | 'outline';
export const Pill: React.FC<{ tone: PillTone }> = ({ tone, children }) => <span className={`hub-pill ${tone}`}>{children}</span>;
